import { BadgeLink } from "@/components/links";
import { StatePage } from "@/components/state-page";
import { getImageUrl, stringToRandomNumber } from "@/utils";
import { orpc } from "@/utils/clients/orpc";
import { CONSTANTS } from "@/utils/constants";
import { requirePermissions, useHasPermission } from "@/utils/permissions";
import { ActionIcon, Anchor, Badge, Button, Card, Skeleton, useModalsStack } from "@mantine/core";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useCanGoBack, useRouter } from "@tanstack/react-router";
import type { AdminEmployee } from "@workspace/admin-domain/src/features/employee/schema";
import dayjs from "@workspace/domain/src/utils/dayjs";
import { t } from "i18next";
import {
  LucideArrowLeft,
  LucideCalendar,
  LucideEdit,
  LucideLock,
  LucideMoreVertical,
  LucidePhone,
  LucideShieldUser,
  LucideToggleLeft,
  LucideUser,
} from "lucide-react";
import EditEmployee from "./-edit";
import EmployeeMenu from "./-menu";

export const Route = createFileRoute("/_authenticated/employees/$id/")({
  component: Component,
  beforeLoad: requirePermissions("employee:get"),
  loader: ({ context, params }) => {
    void context.queryClient.prefetchQuery(
      orpc.employee.get.queryOptions({
        input: { id: params.id },
      }),
    );
  },
  errorComponent: ({ error, reset }) => (
    <StatePage error={error} reset={reset} resourceType="employee" emptyResourceType="employee" />
  ),
  notFoundComponent: () => <StatePage isNotFound resourceType="employee" emptyResourceType="employee" />,
});

function Component() {
  const { id } = Route.useParams();
  const router = useRouter();
  const canGoBack = useCanGoBack();

  const stack = useModalsStack(["form", "confirm"]);

  const {
    data: employee,
    isLoading,
    error,
    refetch,
  } = useQuery(
    orpc.employee.get.queryOptions({
      input: { id },
    }),
  );

  if (isLoading) {
    return <EmployeeSkeleton />;
  }

  if (error) {
    return <StatePage error={error} reset={refetch} resourceType="employee" emptyResourceType="employee" />;
  }

  if (!employee) {
    return null;
  }

  return (
    <div className="gap-md flex flex-col">
      {/* Header with breadcrumbs and actions */}
      <div className="flex items-center justify-between">
        {canGoBack ? (
          <Anchor component="button" onClick={() => router.history.back()} className="flex items-center gap-1.5">
            <LucideArrowLeft className="size-6" />
          </Anchor>
        ) : null}

        <div className="flex items-center gap-1.5">
          <EditEmployee employee={employee} stack={stack} />
          <Button
            onClick={() => stack.open("form")}
            size="sm"
            variant="filled"
            leftSection={<LucideEdit className="size-4" />}
          >
            {t("edit")}
          </Button>

          <EmployeeMenu
            employee={employee}
            target={
              <ActionIcon size="lg" variant="default" className="size-9">
                <LucideMoreVertical className="size-4" />
              </ActionIcon>
            }
          />
        </div>
      </div>

      {/* Main layout - sidebar */}
      <div className="gap-md lg:gap-lg flex flex-col lg:flex-row">
        {/* Left sidebar with main employee information */}
        <div className="gap-md flex w-full flex-col lg:w-92 lg:shrink-0">
          <EmployeeMainCard employee={employee} />
        </div>
      </div>
    </div>
  );
}

function EmployeeMainCard({ employee }: { employee: AdminEmployee["Employee"] }) {
  const hasRolePermission = useHasPermission(true);
  return (
    <Card withBorder className="overflow-visible p-0">
      {/* Avatar Section */}
      <div className="z-10 -m-px flex">
        <div className="dark:bg-primary-800 bg-primary-500 relative h-32 w-full rounded-t-md">
          <div className="absolute inset-x-0 bottom-0 h-16 bg-linear-to-t from-black/20 via-black/10 to-transparent" />
          <div className="dark:bg-dark-500 absolute bottom-0 left-1/2 z-10 flex size-20 -translate-x-1/2 translate-y-1/2 items-center justify-center overflow-hidden rounded-full bg-gray-100">
            {employee.imageUrl ? (
              <img
                src={getImageUrl(employee.imageUrl)}
                alt={employee.name}
                className="h-full w-full rounded-full object-cover"
                draggable={false}
              />
            ) : (
              <LucideUser className="text-dimmed size-10" />
            )}
          </div>
        </div>
      </div>

      {/* Employee Details */}
      <div className="gap-md p-md flex flex-col pt-12">
        <div className="text-center">
          <h1 className="text-xl font-semibold">{employee.name}</h1>
        </div>

        <div className="gap-sm flex items-start">
          <LucideLock className="text-dimmed size-4 shrink-0" />
          <div className="flex flex-col gap-1.5">
            <div className="text-dimmed text-sm font-medium">{t("employees.username")}</div>
            <p className="font-medium">{employee.username}</p>
          </div>
        </div>

        <div className="gap-sm flex flex-col">
          <div className="gap-sm flex items-start">
            <LucidePhone className="text-dimmed size-4 shrink-0" />
            <div className="flex flex-col gap-1.5">
              <div className="text-dimmed text-sm font-medium">{t("employees.phone")}</div>
              {employee.phoneNumber ? (
                <Anchor dir="ltr" href={`tel:${employee.phoneNumber}`} className="font-medium">
                  {employee.phoneNumber}
                </Anchor>
              ) : (
                <span className="text-dimmed">—</span>
              )}
            </div>
          </div>

          <div className="gap-sm flex items-start">
            <LucideShieldUser className="text-dimmed size-4 shrink-0" />
            <div className="flex flex-col gap-1.5">
              <div className="text-dimmed text-sm font-medium">{t("employees.role")}</div>
              <BadgeLink
                to="/roles"
                size="md"
                className="w-fit cursor-pointer"
                enabled={hasRolePermission}
                variant="light"
                search={{ search: employee.role.id }}
                color={
                  CONSTANTS.MANTINE_COLORS_MAP[
                    stringToRandomNumber(employee.role.name, 0, CONSTANTS.MANTINE_COLORS_MAP.length - 1)
                  ]
                }
              >
                {employee.role.name}
              </BadgeLink>
            </div>
          </div>

          <div className="gap-sm flex items-start">
            <LucideToggleLeft className="text-dimmed size-4 shrink-0" />
            <div className="flex flex-col gap-1.5">
              <div className="text-dimmed text-sm font-medium">{t("employees.status")}</div>
              <Badge variant="light" color={employee.isActive ? "green" : "gray"} size="md" className="w-fit">
                {employee.isActive ? t("employees.active") : t("employees.inactive")}
              </Badge>
            </div>
          </div>

          <div className="gap-sm flex items-start">
            <LucideCalendar className="text-dimmed size-4 shrink-0" />
            <div className="flex flex-col gap-1.5">
              <div className="text-dimmed text-sm font-medium">{t("employees.dateJoined")}</div>
              <p className="font-medium">{dayjs(employee.createdAt).format("DD MMM YYYY")}</p>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
}

function EmployeeSkeleton() {
  return (
    <div className="gap-md flex flex-col">
      {/* Header with breadcrumbs and actions */}
      <div className="flex items-center justify-between">
        <Skeleton className="size-6 rounded" />
        <div className="flex items-center gap-1.5">
          <Skeleton className="h-9 w-20" />
          <Skeleton className="size-9 rounded" />
        </div>
      </div>

      {/* Main layout - sidebar */}
      <div className="gap-md lg:gap-lg flex flex-col lg:flex-row">
        {/* Left sidebar */}
        <div className="gap-md flex w-full flex-col lg:w-92 lg:shrink-0">
          <Card withBorder className="overflow-hidden p-0">
            <Skeleton className="h-32 w-full rounded-b-none" />
            <div className="gap-md p-md flex flex-col pt-12">
              <div className="text-center">
                <Skeleton className="mx-auto h-6 w-32" />
              </div>
              <div className="gap-sm mt-1.5 flex flex-col">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="gap-sm flex items-start">
                    <Skeleton className="size-4 shrink-0 rounded-full" />
                    <div className="flex grow flex-col gap-1.5">
                      <Skeleton className="h-4 w-16" />
                      <Skeleton className="h-4 w-1/2" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
